/* eslint-disable react-hooks/exhaustive-deps */
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import mondaySdk from 'monday-sdk-js'
import Loader from 'monday-ui-react-core/dist/Loader'
import { useGet, useUpdateAccount, useUpdateAccountKey } from './hooks/useQuery'
import { Route, Routes } from 'react-router-dom'
import { mondayService } from './services/mondayService'
import { ErrorMessage } from './components/ErrorMessage'
import { restrcitorService } from './services/restrictorService'
import { Provider, ErrorBoundary } from '@rollbar/react' // Provider imports 'rollbar'

const RestrictionCreator = lazy(() => import('./views/RestrictionCreator'))
const ExistingRestrictions = lazy(() => import('./views/ExistingRestrictions'))
const NavBar = lazy(() => import('./components/NavBar'))
const Footer = lazy(() => import('./components/Footer'))

const monday = mondaySdk()

export default function App() {
  const [boards, setBoards] = useState([])
  const [accountInformation, setAccountInformation] = useState({
    accountId: null,
    mondayDomain: null,
    company: null,
    userId: null,
    userEmail: null,
    userName: null,
    mondayNumOfUsers: null,
  })
  const [accessToken, setAccessToken] = useState()
  const {
    isLoading,
    data: restrictionAccount,
    isError: isErrorGetRestrictions,
  } = useGet('boardRestrictions', {
    accountId: accountInformation.accountId,
    accessToken,
  })
  const { mutate: updateMutate, isError: isErrorUpdate } = useUpdateAccount('boardRestrictions', true)
  const { mutate: updateMutateAccountKey, isError: isErrorUpdateKey } = useUpdateAccountKey('boardRestrictions', true)
  const [restrictions, setRestrictions] = useState()
  const [isError, setIsError] = useState(false)
  const [isErrorMondayAPI, setIsErrorMondayAPI] = useState(false)
  const [license, setLicense] = useState(null)
  const [darkMode, setDarkMode] = useState(false)
  const [isShowCurrBoardOnly, setIsShowCurrBoardOnly] = useState()
  const [isShowSubBoards, setIsShowSubBoards] = useState()
  const [isMultipleGroup, setIsMultipleGroup] = useState()
  const [currentBoards, setCurrentBoards] = useState([])
  const [currentBoardsId, setCurrentBoardsId] = useState([])
  const [appMajorVersion, setAppMajorVersion] = useState(null)
  const [appType, setAppType] = useState(null)

  const [currentGroup, setCurrentGroup] = useState(null)
  const [currentGroupId, setCurrentGroupId] = useState(null)

  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isLoadingRestrictions, setIsLoadingRestrictions] = useState(false)

  const [isAppSeatsAvailable, setIsAppSeatsAvailable] = useState(true)

  const isZeroEmptyRef = useRef(null)

  const getAccountLicense = async () => {
    const query = `query {
      app_subscription {
        plan_id
        is_trial
        billing_period
        days_left
      }
    }`
    const res = await monday.api(query, { apiVersion: '2024-10' })

    const currLicense = res.data.app_subscription[0]
    console.log('res.data sub', res.data.app_subscription[0])

    if (currLicense?.plan_id?.includes('seats')) {
      const isAppSeatsAvailable = await checkSeatsAvailability(currLicense.plan_id)
      setIsAppSeatsAvailable(isAppSeatsAvailable)
    }

    setLicense(currLicense || null)
  }

  const checkSeatsAvailability = async (planId) => {
    const query = `query {
      apps_monetization_info {
        seats_count
      }
    }`
    const res = await monday.api(query, { apiVersion: '2024-10' })
    const seatsCount = res.data.apps_monetization_info.seats_count
    process.env.NODE_ENV === 'development' && console.log('seatsCount', seatsCount);
    // In case when the current amount of users in the account passed the purchased seats amount - block app view - user need to upgrade license
    let isAppAvailable = true

    switch (planId) {
      case 'max_seats_100_plan_id':
        if (seatsCount > 100) isAppAvailable = false
        break
      case 'max_seats_200_plan_id':
        if (seatsCount > 200) isAppAvailable = false
        break
      case 'max_seats_500_plan_id':
        if (seatsCount > 500) isAppAvailable = false
        break
      case 'max_seats_800_plan_id':
        if (seatsCount > 800) isAppAvailable = false
        break
      default:
        break
    }

    console.log('file: App.js:118 -> isAppAvailable:', isAppAvailable)
    return isAppAvailable
  }

  const getAccessToken = async () => {
    try {
      const res = await monday.get('sessionToken')
      setAccessToken(res.data)
    } catch (err) {
      console.log('file: App.js:84 -> err:', err)
      setIsErrorMondayAPI(true)
    }
  }

  useEffect(() => {
    getAccessToken()
    getAccountLicense()
    // Get information on dark/light mode and the current boardId
    monday.listen('context', async (res) => {
      try {
        setAppMajorVersion(res.data?.appVersion?.versionData?.major || 7)
        setAppType(res.data.appFeature.type)
        const html = document.querySelector('html')
        if (appType === 'AppFeatureGroupMenuAction') setCurrentGroupId(res.data.groupId) // if the app is opened from the group, get the groupId to automatically choose it.
        setCurrentBoardsId(res.data.boardIds || [res.data.boardId] || [])
        if (res.data.theme === 'dark' || res.data.theme === 'night' || res.data.theme === 'hacker' || res.data.theme === 'hacker_theme' || res.data.theme === 'black') {
          html.classList.remove('light-mode')
          html.classList.add('dark-mode')
          setDarkMode(true)
        } else {
          html.classList.remove('dark-mode')
          html.classList.add('light-mode')
          setDarkMode(false)
        }
      } catch (err) {
        setIsErrorMondayAPI(true)
      }
    })
    if (appType === 'AppFeatureDashboardWidget') {
      //Settings on the dashboard, to decide if filter to the only current board to display, or display all boards
      monday.listen('settings', async (res) => {
        try {
          accountInformation.accountId && updateIsZeroEmpty(res.data.isZeroEmpty)
          setIsShowCurrBoardOnly(res.data.isOnlyCurrBoard)
          setIsShowSubBoards(res.data.isShowSubBoards)
          setIsMultipleGroup(res.data.isMultipleGroup)
        } catch (err) {
          setIsErrorMondayAPI(true)
        }
      })
    } else {
      setIsShowCurrBoardOnly(true)
    }
  }, [accountInformation.accountId])

  useEffect(() => {
    if (!(isShowCurrBoardOnly === undefined && appType === 'AppFeatureDashboardWidget') && !(isShowSubBoards === undefined && appType === 'AppFeatureDashboardWidget') && appMajorVersion) {
      getMondayDetails(isShowSubBoards)
    }
  }, [isShowCurrBoardOnly, appMajorVersion, isShowSubBoards, appType])

  useEffect(() => {
    console.log("restrictionAccount", restrictionAccount);

    if (restrictionAccount) {
      getRestrictions()
      if (!restrictionAccount.mondayDomain && accountInformation.mondayDomain) {
        restrictionAccount.mondayDomain = accountInformation.mondayDomain
        restrictionAccount.company = accountInformation.company
        restrictionAccount.mondayNumOfUsers = accountInformation.mondayNumOfUsers

        if (!restrictionAccount.users.length) {
          restrictionAccount.users.push({
            userId: accountInformation.userId,
            userEmail: accountInformation.userEmail,
            userName: accountInformation.userName,
            totalUserGroupRestricted: 0,
          })
        }
        updateMutate({ restrictionAccount, accessToken })
      }
      if (!restrictionAccount.mondayNumOfUsers && accountInformation.mondayNumOfUsers)
        updateMutateAccountKey({
          key: 'mondayNumOfUsers',
          value: accountInformation.mondayNumOfUsers,
          accountId: restrictionAccount.accountId,
          accessToken,
        })
      if (!restrictionAccount.subscription && license)
        updateMutateAccountKey({
          key: 'subscription',
          value: license,
          accountId: restrictionAccount.accountId,
          accessToken,
        })
    }
  }, [restrictionAccount])


  const updateIsZeroEmpty = async (isZeroEmpty) => {
    if (isZeroEmptyRef.current !== null && isZeroEmpty !== isZeroEmptyRef.current) {
      try {
        await restrcitorService.updateSettingsKey('isZeroEmpty', isZeroEmpty, accountInformation.accountId, accessToken)
      } catch (error) {
        console.log('🚀 ~ file: App.js:134 ~ updateIsZeroEmpty ~ error:', error)
      }
    }
    isZeroEmptyRef.current = isZeroEmpty
  }

  useEffect(() => {
    if (isShowCurrBoardOnly && currentBoardsId) {
      mondayService.getCurrentBoards(currentBoardsId, setCurrentBoards)
    }
  }, [isShowCurrBoardOnly, currentBoardsId])

  useEffect(() => {
    if (currentGroupId && currentBoardsId?.length) {
      mondayService.getCurrentGroup(currentGroupId, currentBoardsId, setCurrentGroup)
    }
  }, [currentGroupId, currentBoardsId])

  const getRestrictions = useCallback(async () => {
    setIsLoadingRestrictions(true)
    try {
      const restrictions = await mondayService.getRestrictionLabels(restrictionAccount.restrictions, accountInformation.accountId, accessToken)
      setRestrictions(restrictions)
    } catch (err) {
      setIsError(true)
    } finally {
      setIsLoadingRestrictions(false)
    }
  }, [restrictionAccount?.restrictions, accountInformation.accountId, accessToken])

  const getMondayDetails = async (isShowSubBoards) => {
    try {
      setIsLoadingDetails(true)
      const accountPromise = mondayService.getAccount(setAccountInformation)
      setBoards([])
      const boardsPromise = mondayService.getBoards(setBoards, appMajorVersion, isShowSubBoards)
      await Promise.all([accountPromise, boardsPromise])
    } catch (err) {
      setIsErrorMondayAPI(true)
    } finally {
      setIsLoadingDetails(false)
    }
  }


  const boardsForDropdown = useMemo(() => {
    if (boards?.length && appMajorVersion) {
      let mappedBoards = []
      if (appMajorVersion >= 8) {
        mappedBoards = boards.map((board) => {
          return {
            tooltipProps: {
              content: (
                <div>
                  {board.workspace && (
                    <>
                      <span>
                        Workspace name: <b>{board.workspace.name}</b>
                      </span>
                      <br />
                    </>
                  )}
                  Board ID: <b>{board.id}</b>
                </div>
              ),
              immediateShowDelay: 500,
            },
            label: board.name,
            value: board.id,
            leftIcon: () => (
              <div
                className="workspace-icon"
                style={{
                  backgroundColor: board.workspace?.settings?.icon.color || null,
                }}
              >
                {board.workspace?.settings?.icon.image ? <img className="workspace-dropdown-image" src={board.workspace?.settings?.icon.image} alt="workspace-icon" /> : board.workspace?.name[0] || ''}
              </div>
            ),
          }
        })
      } else {
        mappedBoards = boards.map((board) => {
          return { label: board.name, value: board.id }
        })
      }

      return mappedBoards
    }
  }, [boards, appMajorVersion])

  //valueCreatedForUser from monday.sdk for monday analytics https://developer.monday.com/apps/docs/value-created-event-best-practices
  useEffect(() => {
    if (!isLoading || restrictions) {
      process.env.NODE_ENV === 'development' && console.log('app rendered')
      monday.execute('valueCreatedForUser')
    }
  }, [isLoading, restrictions])

  const filteredRestrictions = (restrictions) => {
    return restrictions?.filter((restriction) => currentBoardsId.includes(restriction.board.value))
  }

  const validateNewRestriction = (restriction, onSetLoadState) => {
    const { board, group, columns } = restriction
    if (board.label && board.value && group.label && group.value && columns.length) {
      const isExists = checkExistence(restriction)
      if (isExists) {
        monday.execute('notice', {
          message: `You already have an existing restriction on ${board.label} with the group ${group.label}. To enable multiple restrictions for one group activate in settings.`,
          type: 'error', // or "error" (red), or "info" (blue)
          timeout: 10000,
        })
        return false
      } else {
        return true
      }
    } else {
      monday.execute('notice', {
        message: `Couldn't add restriction - some columns are missing`,
        type: 'error', // or "error" (red), or "info" (blue)
        timeout: 10000,
      })
      onSetLoadState && onSetLoadState('edit', false)
      return false
    }
  }
  const checkExistence = (restriction) => {
    if (isMultipleGroup) return false // In case user decided to add multiple groups for Restriction Selector recipe
    const existing = restrictions.find((rest) => Number(rest.board.value) === Number(restriction.board.value) && rest.group.value === restriction.group.value && rest._id !== restriction._id)
    process.env.NODE_ENV === 'development' && console.log(`existing -> existing`, existing)
    return !!existing
  }

  const checkIsEnabledFeatures = (license, dbLicense) => {
    if (license?.is_trial === true || license?.plan_id !== 'free_plan_id' || dbLicense !== 'free') return true

    return false
  }

  const isEnabledFeatures = useMemo(() => {
    if (restrictionAccount?.license) return checkIsEnabledFeatures(license, restrictionAccount?.license)
  }, [license, restrictionAccount?.license])

  // const loadingState = isLoading
  const loadingState = isLoading || (appType === 'AppFeatureGroupMenuAction' && !currentGroup && !currentBoards?.length)

  const getRollbarConfig = () => {
    const { company, accountId, userId, userEmail, mondayDomain } = accountInformation

    const rollbarConfig = {
      accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
      environment: process.env.NODE_ENV,
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        person: {
          id: accountId,
          userId,
          company,
          userEmail,
          mondayDomain,
        },
      },
    }

    return rollbarConfig
  }


  if (isErrorMondayAPI) return <ErrorMessage title={'Attention'} error={'Error occurred. Please make sure you have the correct permissions on this account.'} />
  else if (isError || isErrorGetRestrictions || isErrorUpdate || isErrorUpdateKey) return <ErrorMessage error={'Error occurred. Please try to refresh the page or contact us on appsupport@unitask-inc.com'} />
  else if (loadingState)
    return (
      <div className="loader-div">
        <div className="loader">
          <Loader />
        </div>
      </div>
    )
  else if (!isAppSeatsAvailable)
    return (
      <ErrorMessage
        title={'Attention'}
        error={
          <p>
            We have detected that your current number of seats does not meet the requirements of your plan. <br />{' '}
            <span className="link" onClick={() => monday.execute('openPlanSelection', { isInPlanSelection: true })}>
              Upgrade your license to continue using the app.
            </span>
          </p>
        }
      />
    )
  else if (currentBoardsId.length === 0) return <ErrorMessage title={'Attention'} error={'Connect a board to start using the app.'} />
  return (
    <Provider config={getRollbarConfig()}>
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="loader-div">
              <div className="loader">
                <Loader />
              </div>
            </div>
          }
        >
          <div className={darkMode ? 'App darkmode' : 'App'}>
            <NavBar license={license} dbLicense={restrictionAccount.license} monday={monday} appType={appType} />
            <Routes>
              <Route
                path="/"
                element={
                  <RestrictionCreator
                    existingRestrictions={restrictionAccount.restrictions}
                    boardsForDropdown={boardsForDropdown}
                    boards={boards}
                    monday={monday}
                    validateNewRestriction={validateNewRestriction}
                    company={accountInformation?.company}
                    accountId={restrictionAccount.accountId}
                    accessToken={accessToken}
                    setIsError={setIsError}
                    isEnabledFeatures={isEnabledFeatures}
                    currentBoards={isShowCurrBoardOnly ? currentBoards : []}
                    isLoadingDetails={isLoadingDetails}
                    appType={appType}
                    currentGroup={currentGroup}
                  />
                }
              />
              <Route
                path="/existing"
                element={
                  <ExistingRestrictions
                    boardsForDropdown={boardsForDropdown}
                    restrictions={isShowCurrBoardOnly ? filteredRestrictions(restrictions) : restrictions}
                    setRestrictions={setRestrictions}
                    validateNewRestriction={validateNewRestriction}
                    monday={monday}
                    accountId={restrictionAccount.accountId}
                    accessToken={accessToken}
                    setIsError={setIsError}
                    isEnabledFeatures={isEnabledFeatures}
                    currentBoards={isShowCurrBoardOnly ? currentBoards : []}
                    appType={appType}
                    accountRestrictionsLength={restrictionAccount.restrictions.length}
                    isLoadingRestrictions={isLoadingRestrictions}
                  />
                }
              />
              <Route
                path="/howto"
                element={
                  <>
                    <iframe title="howto" src="https://live8-service-9665740-f20d69c3.us.monday.app/howto/" />
                  </>
                }
              />
            </Routes>
            {appType === 'AppFeatureDashboardWidget' && <Footer darkMode={darkMode} />}
          </div>
        </Suspense>
      </ErrorBoundary>
    </Provider>
  )
}
